import { useIsFirstGigPost } from "api/clients/locationsAndOrganizations";
import { LoaderLogo } from "components/Loader";
import { MaintenanceModeDialog, maintenanceRoutes } from "components/MaintenanceModeDialog";
import { useAtomValue } from "jotai";
import { useAuth } from "providers/Auth";
import { useFeatureFlag } from "providers/LaunchDarkly";
import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import { activeLicenseAtom, useAuthenticatedStateSetup } from "services/globalState";
import { ROUTES, buildRoute } from "./Routes";

export const SignedUpRouter: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signUpRequired, linkableAccountsStatus, locationRequired } = useAuth();

  const [signUpParams] = useTypedSearchParams(ROUTES.SIGN_UP);

  const maintenanceModeEnabled =
    useFeatureFlag("maintenance-mode") && maintenanceRoutes.some((r) => location.pathname.startsWith(r));

  const locationSetupLoading = useAuthenticatedStateSetup();

  const activeLicense = useAtomValue(activeLicenseAtom);
  React.useEffect(() => {
    if (activeLicense === "hire") {
      import("./HireRouter");
    } else if (activeLicense === "marketplace") {
      import("./MarketplaceRouter");
    }
  }, [activeLicense]);

  useEffect(() => {
    if (!signUpRequired && locationRequired) {
      navigate(buildRoute(ROUTES.LOCATIONS, {}));
    }
  }, [locationRequired, signUpRequired, navigate]);

  const isInitialGigPost = useIsFirstGigPost();
  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate changes on route changes but this should only happen once
  useEffect(() => {
    if (!isInitialGigPost) return;
    const route = buildRoute(ROUTES.LOCATIONS.DETAILS.GIG_VIEWS.SCHEDULE, {}, { post_gig_type: "single" });
    navigate(route);
  }, [isInitialGigPost]);

  if (signUpRequired && linkableAccountsStatus !== "linking-required")
    return <Navigate to={buildRoute(ROUTES.SIGN_UP, {}, signUpParams)} />;
  if (locationSetupLoading) return <LoaderLogo />;

  return (
    <>
      <MaintenanceModeDialog open={maintenanceModeEnabled} onBack={() => navigate(-1)} />
      <Outlet />
    </>
  );
};
